import { formatNumber } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  CreateAllocationHeader,
  CreateAllocationResponse,
  EligibleAllocation,
  KeyValue,
  StrictAllocations,
  SubAccount,
  Trade
} from '@approot/models';
import {
  CreateAllocationParametersPayload,
  TradesCaptureService
} from '@approot/services';
import { SubscriptionSink, Utilities } from '@approot/utilities';
import { cloneDeep, isEqual } from 'lodash';
import { distinctUntilChanged } from 'rxjs/operators';
import {
  AllocateTradeService,
  CompletionStatus
} from './allocate-trade.service';
import { AllocateTradeState } from './allocate-trade.types';
type State = '' | 'LOADING' | 'DONE';
type ExpectedSum = { expectedSum: number; sum: number };
@Component({
  selector: 'app-allocate-trade',
  templateUrl: './allocate-trade.component.html',
  styleUrls: ['./allocate-trade.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AllocateTradeService]
})
export class AllocateTradeComponent implements OnInit, OnDestroy {
  @Input() public subAccounts: SubAccount[];
  @Input() public eligibleAllocations: EligibleAllocation[] = [];
  @Input() public selectedTrade: Trade;
  @Input() public strictAllocations: StrictAllocations;
  @Output() public returnToMenu = new EventEmitter<true>();

  public counterParties: string[] = [];
  public splitAccordinglyToValues: string[] = [];
  public form: FormGroup;
  public saveState: State = '';
  public previewState: State = '';
  public expectedSums: Record<string, ExpectedSum>[] = [];
  public selectedRowIndex: number;
  public splitMethods = ['Proportional'];
  public createResponse: CreateAllocationResponse;
  public readonly done = 'DONE';
  public readonly loading = 'LOADING';
  public state: AllocateTradeState;

  private readonly listBelow = 'List Below';
  private readonly subsink = new SubscriptionSink();
  private readonly tradesCaptureService = inject(TradesCaptureService);
  private readonly changeRef = inject(ChangeDetectorRef);
  private readonly elemRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly allocateTradeService = inject(AllocateTradeService);

  public ngOnInit(): void {
    this.subsink.sink = this.allocateTradeService.stateChanged
      .pipe(distinctUntilChanged((a, b) => isEqual(a, b)))
      .subscribe(state => {
        this.state = state;
        this.changeRef.markForCheck();
      });

    this.counterParties = [
      this.selectedTrade.counterPartyA,
      this.selectedTrade.counterPartyB,
      this.listBelow
    ];

    if (this.selectedTrade.notional != null) {
      this.splitAccordinglyToValues.push('Notional');
    }

    if (this.selectedTrade.counterNotional != null) {
      this.splitAccordinglyToValues.push('Counter Notional');
    }

    const splitAccordinglyTo =
      this.selectedTrade.faceCurrency ===
      this.selectedTrade.cross?.substring(0, 3)
        ? this.splitAccordinglyToValues[0]
        : this.splitAccordinglyToValues[1];

    this.form = new FormGroup(
      {
        counterPartyA: new FormControl(this.listBelow, Validators.required),
        counterPartyB: new FormControl(
          this.selectedTrade.counterPartyB,
          Validators.required
        ),
        splitAccordinglyTo: new FormControl(
          splitAccordinglyTo,
          Validators.required
        ),
        allocations: new FormArray([], [Validators.minLength(1)]),
        splitMethod: new FormControl(this.splitMethods[0], Validators.required),
        splitRelatedTrades: new FormControl(true)
      },
      [this.counterPartiesValidation.bind(this)]
    );

    this.expectedSums = [
      {
        [this.splitAmountShortName]: {
          expectedSum: this.getExpectAmountSum,
          sum: 0
        }
      }
    ];

    (this.form.get('allocations') as FormArray).push(
      new FormArray([this.createNewAllocation(0)], [Validators.minLength(1)])
    );
  }

  public ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  get allocationTablesControls() {
    return this.allocationTables.controls as FormArray[];
  }

  public get previewAllocationsDisabled() {
    return this.invalidTables || this.previewState;
  }

  public get splitAmountShortName(): 'notional' | 'counterNotional' {
    return this.form.get('splitAccordinglyTo').value === 'Notional'
      ? 'notional'
      : 'counterNotional';
  }

  public onPaste(
    event: ClipboardEvent,
    index: number,
    columnName: string,
    tableIndex: number
  ) {
    const textPasted = event.clipboardData.getData('text/plain');
    if (textPasted.includes('\t') || textPasted.includes('\n')) {
      event.preventDefault();
      const allocationsFormArray = (this.form.get('allocations') as FormArray)
        .controls[tableIndex] as FormArray;
      let columns = [];
      const isTable1 = tableIndex === 0;
      if (isTable1) {
        this.eligibleAllocations.forEach(allocation => {
          columns.push(allocation.key);
        });
      }
      if (this.createResponse) {
        columns.push(
          ...this.createResponse.headers
            .filter(header => this.expectedSums[tableIndex][header.shortName])
            .map(header => header.shortName)
        );
      } else {
        columns.push(this.splitAmountShortName);
      }
      const columnStartingIndex = columns.findIndex(
        column => column === columnName
      );
      const columnsToUse = columns.slice(
        columns.indexOf(columnName),
        columns.length
      ); // the columns we reference that we will use later
      let splitTextRow = textPasted.trimEnd().split(/\r?\n/); // split the text by new lines
      if (splitTextRow.length) {
        splitTextRow.forEach((row, rowIndex) => {
          // go through each row
          const curRowIndex = index + rowIndex;

          if (curRowIndex >= allocationsFormArray.length) {
            this.addAllocation();
          }
          const rowGroup = allocationsFormArray.controls[
            curRowIndex
          ] as FormGroup;

          let skipEligibleAllocations = false;

          row
            .split('\t')
            .forEach((columnValue: any, columnForIndex: number) => {
              // go through each column
              const currentColumnIndex = columnStartingIndex + columnForIndex;
              const isPartOfEligibleAllocations =
                isTable1 &&
                currentColumnIndex < this.eligibleAllocations.length;

              columnValue = isPartOfEligibleAllocations
                ? columnValue
                : Utilities.formattedNumberToNumber(columnValue);

              if (columnForIndex < columnsToUse.length) {
                const columnControl = rowGroup.get(
                  columnsToUse[columnForIndex]
                );

                if (isTable1 && isPartOfEligibleAllocations) {
                  if (skipEligibleAllocations) {
                    return;
                  }
                  const keyAndValue =
                    this.eligibleAllocations[currentColumnIndex];

                  if (!keyAndValue.values.includes(columnValue)) {
                    skipEligibleAllocations = true;
                    this.eligibleAllocations.forEach(k => {
                      const eligibleCtrl = rowGroup.get(k.key);
                      eligibleCtrl.setValue('');
                      eligibleCtrl.markAsDirty();
                    });
                    return;
                  }
                }
                columnControl.setValue(columnValue);
                columnControl.markAsDirty();
                columnControl.markAsTouched();
              }
            });
        });
      }
      columnsToUse.forEach(column => {
        this.calculateFieldSum(column, tableIndex);
      });
    }
  }

  public changeInAmountShortName() {
    const oldAmountName =
      this.splitAmountShortName === 'notional' ? 'counterNotional' : 'notional';

    this.allocationTables.controls.forEach((formArray: FormArray) => {
      formArray.controls.forEach((row: FormGroup) => {
        if (row.get(oldAmountName)) {
          const newControl = new FormControl(row.get(oldAmountName).value);
          if (row.get(oldAmountName).touched) {
            newControl.markAllAsTouched();
          }
          row.addControl(this.splitAmountShortName, newControl);
          row.removeControl(oldAmountName);
        }
      });
    });

    this.changeExpectedSumAmounts();
    this.changeRef.markForCheck();
  }

  public get getExpectAmountSum(): number {
    return this.selectedTrade[this.splitAmountShortName];
  }

  public calculateFieldSum(field: string, tableIndex: number) {
    if (this.expectedSums[tableIndex][field]) {
      this.expectedSums[tableIndex][field].sum = this.allocationTables.controls[
        tableIndex
      ].value.reduce((sum, cur) => {
        const formattedNumber = cur[field]
          ? Utilities.formattedNumberToNumber(cur[field])
          : 0;
        return +(sum + formattedNumber).toFixed(
          Math.max(
            (sum.toString().split('.')[1] || []).length,
            (formattedNumber.toString().split('.')[1] || []).length
          )
        );
      }, 0);
    }
  }

  public get allocationTables(): FormArray {
    return this.form.get('allocations') as FormArray;
  }

  public get firstAllocationsLength(): number {
    return (this.allocationTables.controls[0] as FormArray).length;
  }

  public addAllocation(): void {
    if (this.previewState !== this.loading) {
      this.allocationTables.controls.forEach((table: FormArray, i: number) => {
        table.push(this.createNewAllocation(i));
      });

      setTimeout(() => {
        const tableContainers =
          this.elemRef.nativeElement.querySelectorAll('.table-container');
        tableContainers.forEach(e => {
          this.renderer.setProperty(e, 'scrollTop', e.scrollHeight);
        });
      }, 0);
    }
  }

  public removeAllocations() {
    this.allocationTables.controls.forEach((table: FormArray, i: number) => {
      table.removeAt(this.selectedRowIndex);
      Object.keys(this.expectedSums[i]).forEach((key: string) => {
        this.calculateFieldSum(key, i);
      });
    });
    this.selectedRowIndex = null;
  }

  public goBack() {
    this.returnToMenu.emit(true);
  }

  public get splitAccordingTo(): string {
    return this.form.get('splitAccordinglyTo').value;
  }

  public get formValidity(): boolean {
    return this.form.valid;
  }

  public previewAllocations() {
    this.previewState = this.loading;
    let allocations = {};
    const subAccountsAllocationKey = this.eligibleAllocations[0].key;
    (this.form.get('allocations') as FormArray).controls[0].value.forEach(
      (allocation: KeyValue & { notional?: string }) => {
        allocations[allocation[subAccountsAllocationKey]] =
          Utilities.formattedNumberToNumber(
            allocation[this.splitAmountShortName]
          );
      }
    );
    this.form.disable();
    const isCounterPartyAList =
      this.form.get('counterPartyA').value === this.listBelow;

    const payload = {
      task: 'createAllocationParameters',
      item: this.selectedTrade,
      allocationsAttribute: this.splitAmountShortName,
      allocations,
      subAccountsAllocationKey,
      subAccounts: this.subAccounts,
      splitMethod: this.form.get('splitMethod').value,
      splitRelatedTrades: this.form.get('splitRelatedTrades').value,
      [isCounterPartyAList ? 'newCounterPartyB' : 'newCounterPartyA']:
        this.form.get(isCounterPartyAList ? 'counterPartyB' : 'counterPartyA')
          .value
    } as CreateAllocationParametersPayload;

    this.tradesCaptureService.createAllocationParameters(payload).subscribe({
      next: (response: CreateAllocationResponse) => {
        this.createResponse = response;
        this.createResponse.relatedTrades = [
          response.trade,
          ...this.createResponse.relatedTrades.filter(
            trade => trade.tradeId !== response.trade.tradeId
          )
        ];
        this.createResponse.relatedTrades.forEach(
          (_, relatedTradesIndex: number) => {
            this.setResponseExpectedSums(relatedTradesIndex, response);
            this.addRelatedTradesAllocations(response, relatedTradesIndex);
          }
        );
        this.form.get('allocations').enable();
        setTimeout(() => {
          this.handleScroll();
          this.previewState = this.done;
          this.changeRef.markForCheck();
        }, 0);
      },
      error: () => {
        this.previewState = '';
        this.form.enable();
        this.changeRef.markForCheck();
      }
    });
  }

  public get invalidTables() {
    return (
      !this.formValidity ||
      !this.expectedSums.every(table =>
        Object.keys(table).every((key: string) => {
          const val = table[key];
          if (this.strictAllocations[key] === false) {
            return true;
          }
          return val.expectedSum !== val.sum ? false : true;
        })
      )
    );
  }

  public FieldValidators(
    format: CreateAllocationHeader['format']
  ): null | 'negativeNumericShorthand' {
    if (format === 'formattedDecimal') {
      return 'negativeNumericShorthand';
    }
    return null;
  }

  public shortNameToLongName(field: string): string {
    return this.createResponse.headers.find(
      header => header.shortName === field
    ).longName;
  }

  public formatNumberFromField(field: string, value: number): string | number {
    if (this.previewState === '') {
      return formatNumber(value, 'en-GB', `1.2-2`);
    }
    const header = this.createResponse.headers.find(
      header => header.shortName === field
    );
    switch (header.format) {
      case 'formattedDecimal':
        return this.formatHeaderDecimal(value, header);
      case 'formattedNumber':
        return value;
    }
  }

  public sumDiff(val: ExpectedSum): string {
    // returns formatted difference of the expected sum and the current sum
    const sumDiff = Math.abs(val.expectedSum - val.sum);
    const decimalPlaces = Math.max(
      2,
      (val.sum.toString().split('.')[1] || []).length,
      (val.expectedSum.toString().split('.')[1] || []).length
    ); // used to prevent floating point subtraction issues
    return formatNumber(
      sumDiff,
      'en-GB',
      `1.${decimalPlaces}-${decimalPlaces}`
    );
  }

  public formatHeaderDecimal(
    value: number,
    header?: CreateAllocationHeader
  ): string {
    const decimalPlaces = Math.max(
      +(header?.nDecimals ?? 0),
      (value.toString().split('.')[1] || []).length
    );
    return formatNumber(value, 'en-GB', `1.${decimalPlaces}-${decimalPlaces}`);
  }

  public formatHeaderValue(
    header: CreateAllocationHeader,
    value: any
  ): string | number {
    switch (header.format) {
      case 'formattedDecimal':
        return value ? Utilities.formattedNumberToNumber(value) : 0;
      case 'formattedNumber':
        return value ? +value : 0;
      default:
        return value;
    }
  }

  public async saveAllocations() {
    this.saveState = this.loading;
    let allocationParameters = cloneDeep(this.createResponse);
    const allocations = this.allocationTables.value;
    allocationParameters.relatedTrades.forEach((trade, index: number) => {
      allocationParameters.headers.forEach(header => {
        if (
          allocationParameters.allocationDetails[trade.tradeId]
            .tradeAllocationDetails[header.shortName]
        ) {
          allocationParameters.allocationDetails[
            trade.tradeId
          ].tradeAllocationDetails[header.shortName] = {};

          allocations[index].forEach((row, rowIndex: number) => {
            allocationParameters.allocationDetails[
              trade.tradeId
            ].tradeAllocationDetails[header.shortName][
              this.createResponse.newCounterParties[rowIndex]
            ] = this.formatHeaderValue(header, row[header.shortName]);
          });
        }
      });
    });

    allocationParameters.newCounterParties =
      this.createResponse.newCounterParties;

    this.allocateTradeService.reset();

    this.subsink.sink = this.allocateTradeService.complete$
      .pipe(distinctUntilChanged())
      .subscribe({
        next: (ok: CompletionStatus) => {
          if (ok === 'success') {
            this.saveState = this.done;
            this.tradesCaptureService.getTrades().subscribe();
          } else {
            this.saveState = '';
          }
          this.changeRef.markForCheck();
        }
      });

    await this.allocateTradeService.runAllocateTrade({
      allocationParameters,
      subAccountsAllocationKey: this.eligibleAllocations[0].key,
      subAccounts: this.subAccounts
    });
  }

  public formatFieldOnBlur(
    header: CreateAllocationHeader,
    value: any,
    formcontrol: FormControl
  ) {
    // on blur number needs to be formatted to correct decimal places
    const cleanValue = Utilities.formattedNumberToNumber(value);
    if (
      header.format === 'formattedDecimal' &&
      header.nDecimals &&
      !isNaN(cleanValue) &&
      value !== ''
    ) {
      const decimalPlaces = Math.max(
        +(header?.nDecimals ?? 0),
        (cleanValue.toString().split('.')[1] || []).length
      );

      formcontrol.setValue(
        formatNumber(cleanValue, 'en-GB', `1.${decimalPlaces}-${decimalPlaces}`)
      );
    }
  }

  private createNewAllocation(tableIndex: number): FormGroup {
    let newGroup = new FormGroup({});
    if (tableIndex === 0) {
      this.eligibleAllocations.forEach(eligibleAllocation => {
        newGroup.setControl(eligibleAllocation.key, new FormControl(''));
        newGroup
          .get(eligibleAllocation.key)
          .setValidators([
            this.eligibleAllocationValidator.bind(this, eligibleAllocation.key),
            Validators.required
          ]);

        this.subsink.sink = newGroup
          .get(eligibleAllocation.key)
          .valueChanges.pipe(distinctUntilChanged())
          .subscribe(value => {
            if (value) {
              const subAccount = this.subAccounts.find(
                account => account[eligibleAllocation.key] === value
              );

              this.eligibleAllocations.forEach(m => {
                if (m.key !== eligibleAllocation.key) {
                  const ctrl = newGroup.get(m.key);
                  ctrl.markAsTouched();
                  ctrl.setValue(subAccount[m.key]);
                }
              });
            }
          });
      });
    }

    Object.keys(this.expectedSums[tableIndex]).forEach(key => {
      newGroup.addControl(key, new FormControl(''));
    });

    return newGroup;
  }

  private changeExpectedSumAmounts() {
    const oldAmountName =
      this.splitAmountShortName === 'notional' ? 'counterNotional' : 'notional';
    this.expectedSums.forEach((sum, index: number) => {
      if (sum[oldAmountName]) {
        sum[this.splitAmountShortName] = {
          expectedSum: index
            ? sum[oldAmountName].expectedSum
            : this.getExpectAmountSum,
          sum: sum[oldAmountName].sum
        };
        delete sum[oldAmountName];
      }
    });
  }

  private setResponseExpectedSums(
    tableIndex: number,
    response: CreateAllocationResponse
  ) {
    if (!this.expectedSums[tableIndex]) {
      this.expectedSums.push({});
    }
    const tradeAllocationTotals =
      response.allocationDetails[response.relatedTrades[tableIndex].tradeId]
        .tradeAllocationTotals;
    Object.entries(tradeAllocationTotals).forEach(
      ([key, value]: [string, number]) => {
        if (!(!tableIndex && key === this.splitAmountShortName)) {
          this.expectedSums[tableIndex][key] = {
            expectedSum: value,
            sum: 0
          };
        }
      }
    );
  }

  private addRelatedTradesAllocations(
    response: CreateAllocationResponse,
    relatedTradesIndex: number
  ) {
    const allocations = this.allocationTables;
    const allocationsTable1 = allocations.controls[0] as FormArray;
    let tableArray = new FormArray([]);
    allocationsTable1.controls.forEach((group: FormGroup, index: number) => {
      let row = new FormGroup({});
      response.headers.forEach(header => {
        if (this.expectedSums[relatedTradesIndex][header.shortName]) {
          const value =
            response.allocationDetails[
              response.relatedTrades[relatedTradesIndex].tradeId
            ].tradeAllocationDetails[header.shortName][
              response.newCounterParties[index]
            ];
          if (
            !relatedTradesIndex &&
            header.shortName !== this.splitAmountShortName
          ) {
            group.addControl(
              header.shortName,
              new FormControl(
                this.formatNumberFromField(header.shortName, value)
              )
            );
          } else if (relatedTradesIndex) {
            row.addControl(
              header.shortName,
              new FormControl(
                this.formatNumberFromField(header.shortName, value)
              )
            );
          }
        }
      });
      if (relatedTradesIndex) {
        tableArray.push(row);
      }
    });
    if (relatedTradesIndex) {
      (this.form.get('allocations') as FormArray).push(tableArray);
    }

    setTimeout(() => {
      response.headers.forEach(column => {
        this.calculateFieldSum(column.shortName, relatedTradesIndex);
      });
      this.changeRef.markForCheck();
    }, 0);
  }

  private handleScroll() {
    // scrolls all tables at the same time
    if (this.createResponse.relatedTrades.length > 1) {
      const scrollHandler = (element: HTMLElement, index: number) => {
        tableContainers.forEach((table: HTMLElement, i: number) => {
          if (i !== index) {
            this.renderer.setProperty(table, 'scrollTop', element.scrollTop);
          }
        });
      };

      const tableContainers =
        this.elemRef.nativeElement.querySelectorAll('.table-container');
      tableContainers.forEach((element: HTMLElement, index: number) => {
        element.onscroll = scrollHandler.bind(this, element, index);
      });
    }
  }

  private eligibleAllocationValidator(
    fieldName: string,
    control: AbstractControl
  ):
    | { entryAlreadyExist: { value: string } }
    | { nonExistentEntity: { value: string } }
    | null {
    const previousValue = control.parent.value[fieldName];
    const currentValue = control.value;
    const doesCounterPartyExist = (
      control.parent.parent as FormArray
    ).controls.filter((formGroup: FormGroup) => {
      const fieldCtrl = formGroup.get(fieldName);
      if (currentValue !== previousValue && previousValue === fieldCtrl.value) {
        // let previous controls of the same previous value update
        fieldCtrl.updateValueAndValidity();
      }
      return fieldCtrl.value === currentValue && fieldCtrl !== control;
    });

    if (doesCounterPartyExist.length) {
      return { entryAlreadyExist: { value: currentValue } };
    }

    return null;
  }

  private counterPartiesValidation(
    control: AbstractControl
  ): { invalidCounterParty: { value: string } } | null {
    const counterPartyA = control.get('counterPartyA').value;
    const counterPartyB = control.get('counterPartyB').value;

    if (counterPartyA === counterPartyB) {
      return {
        invalidCounterParty: { value: 'Counter parties can not be the same' }
      };
    } else if (
      counterPartyA !== this.listBelow &&
      counterPartyB !== this.listBelow
    ) {
      return {
        invalidCounterParty: {
          value: `One counter party must be marked as "${this.listBelow}"`
        }
      };
    }
    return null;
  }
}
